import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import classNames from "classnames";
import CalendarImage from "@assets/icons/calendar.svg";
import { useDashboardStore } from "@pages/Dashboard/store/dashboard.state";
import { formatDateRange } from "src/utils/formatDate";
import { t } from "i18next";
import { CalendarModal } from "@components/ui-kit/calendar-modal";
import CustomSelect from "@components/ui-kit/CustomSelect";
import { useAdsAnaliticsStore } from "src/store/ads-analitics.store";
import { Navigation } from "@pages/AdsAnalitics/components/Header/ui/navigation";
import { ExecutiveSummary } from "@components/ExecutiveSummary";
import { useBackground } from "@components/BackfroundContext";
import BrandRevenueChart from "./BrandRevenueChart";
import MarketOverview from "./DotChart";
import PerformanceBlock from "./BrandBlock";
import MarketLevelSalesDistribution from "./MarketLevelBlock";
import { useMarketStore } from "src/store/market.state";
import { TotalSales } from "./TotalSales";
import { BrandPerformance } from "./BrandPerformance";
import { Products } from "./Products";
import { BrandTable } from "./BrandTable";
import FlowProgressLine from "@components/FlowProgressLine";
import { ActiveStepStore } from "src/store/onboarding.store";
import FlowProgressLineSecond from "@components/FlowProgressLineSecond";
import { useIsOn } from "@hooks/useFeatureToggle";
import { FEATURE_TOGGLES } from "@lib/featureToggles";

export const MarketTitles = [
  "Market Total Sales",
  "Brand Market Share",
  "Market Average Price",
  "Market Units Sold",
  "Market ASIN count",
  "Market Promotion Value",
  "Market Promotion Count",
  "Market Review Score",
  "Market POS",
  "Market Ad Spend",
];

export type Option = {
  id: string;
  text: string;
  img?: string;
  asin?: string;
};

export const COLORSCHART = ["#fc6762", "#6B4DBA", "#5295E0"];

export function MarketIntelligenceBlock() {
  const step = ActiveStepStore((state) => state.step);
  const setStep = ActiveStepStore((state) => state.setStep);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const marketHeaderEnabled = useIsOn(FEATURE_TOGGLES.MARKET_HEADER);
  const marketExecutiveSummaryEnabled = useIsOn(
    FEATURE_TOGGLES.MARKET_EXECUTIVE_SUMMARY,
  );
  const marketMarketLevelChartEnabled = useIsOn(
    FEATURE_TOGGLES.MARKET_MARKET_LEVEL_CHART,
  );
  const marketMarketOverviewChartEnabled = useIsOn(
    FEATURE_TOGGLES.MARKET_MARKET_OVERVIEW_CHART,
  );
  const marketMarketShareChartEnabled = useIsOn(
    FEATURE_TOGGLES.MARKET_MARKET_SHARE_CHART,
  );
  const marketMarketSalesChartEnabled = useIsOn(
    FEATURE_TOGGLES.MARKET_MARKET_SALES_CHART,
  );
  const marketBrandTableEnabled = useIsOn(FEATURE_TOGGLES.MARKET_BRAND_TABLE);
  const marketBrandPerformanceChartEnabled = useIsOn(
    FEATURE_TOGGLES.MARKET_BRAND_PERFORMANCE_CHART,
  );
  const marketTotalSalesChartEnabled = useIsOn(
    FEATURE_TOGGLES.MARKET_TOTAL_SALES_CHART,
  );
  const marketProductsBlockEnabled = useIsOn(
    FEATURE_TOGGLES.MARKET_PRODUCTS_BLOCK,
  );

  const { setIsBlurred } = useBackground();
  const { dateRange, setDateRange, compareType, setCompareType } =
    useDashboardStore((state) => ({
      dateRange: state.dateRange,
      setDateRange: state.setDateRange,
      compareType: state.compareType,
      setCompareType: state.setCompareType,
    }));
  const { setAsinBrandSelect } = useDashboardStore();
  const [selectedDateRange, setSelectedDateRange] = useState(
    formatDateRange(dateRange.startDate, dateRange.endDate),
  );
  const { compareWith, setCompareWith } = useAdsAnaliticsStore();
  const { isWhatIf, setIsWhatIf } = useMarketStore();

  const handleDateChange = (startDate: Date, endDate: Date) => {
    setDateRange({ startDate, endDate });
    setSelectedDateRange(formatDateRange(startDate, endDate));
    setIsCalendarOpen(false);
    setIsBlurred(false);
  };

  const label = ["Market Share", "Metrics Rate", "ASIN Query Rate"];

  useEffect(() => {
    setAsinBrandSelect(false);
  }, []);
  const flowProgressComponent = (() => {
    if (step === 3 || step === 4) {
      return <FlowProgressLine activeStep={step} setActiveStep={setStep} />;
    }
    if (step === 7) {
      return <FlowProgressLineSecond activeStep={step} />;
    }
    return null;
  })();

  return (
    <div>
      {marketHeaderEnabled && (
        <div className={styles.dashboardWidgetsHeader}>
          <div className={styles.navBetween}>
            <Navigation customTitles={label} setIsOpenMenu={() => {}} />
            <h1 className={styles.headerTitle}>{t("Market Intelligence")}</h1>
          </div>
          <div className={styles.buttons}>
            <div className={styles.calendar}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCalendarOpen(!isCalendarOpen);
                  setIsBlurred(!isCalendarOpen);
                }}
                className={classNames(
                  styles.button,
                  isCalendarOpen && styles.active,
                )}
              >
                <img src={CalendarImage} alt="Calendar" />
                {selectedDateRange}
              </button>
              {isCalendarOpen && (
                <div className={styles.modal}>
                  <CalendarModal
                    closeModal={() => {
                      setIsCalendarOpen(false);
                      setIsBlurred(false);
                    }}
                    onDateChange={handleDateChange}
                    initialRange={dateRange}
                  />
                </div>
              )}
            </div>
            <CustomSelect
              dateRange={dateRange}
              compareWith={compareWith}
              setCompareWith={setCompareWith}
              compareType={compareType}
              setCompareType={setCompareType}
              hideSwitch
            />
          </div>
        </div>
      )}
      {flowProgressComponent}

      {marketExecutiveSummaryEnabled && (
        <div className={styles.executiveSummary}>
          <ExecutiveSummary
            hideSwitch={true}
            showOnly="Market"
            showWhatIf={isWhatIf}
            onToggleWhatIf={() => setIsWhatIf(!isWhatIf)}
          />
        </div>
      )}
      {marketMarketLevelChartEnabled && (
        <div className={styles.marketLevel}>
          <MarketLevelSalesDistribution />
        </div>
      )}
      <div className={styles.marketBetween}>
        {marketMarketOverviewChartEnabled && (
          <div className={styles.brandRevenue}>
            <MarketOverview />
          </div>
        )}
        {marketMarketShareChartEnabled && (
          <div className={styles.brandRevenue}>
            <BrandRevenueChart hidePercentage={true} />
          </div>
        )}
      </div>
      <div className={styles.additionalChartsContainer}>
        {marketMarketSalesChartEnabled && <PerformanceBlock />}
        {marketBrandTableEnabled && <BrandTable isWhatIf={isWhatIf} />}
      </div>
      {marketBrandPerformanceChartEnabled && <BrandPerformance />}
      {marketTotalSalesChartEnabled && <TotalSales />}
      {marketProductsBlockEnabled && <Products />}
    </div>
  );
}
